<template>
    <v-container fluid class="pa-0">
        <v-row v-if="selectedUserMeasurementSet === null">
            <v-col>
                <v-card style="margin-bottom: 2px">
                    <v-card-title primary-title>
                        <v-row>
                            <v-col>
                                <h3 class="text-h5 pl-3">Data QC Dashboard</h3>
                                <p v-if="!qcSummary.length" class="pl-3 mb-0 text-body-1">
                                    Select one or more schema configs and click "Run Query" to view
                                    data
                                </p>
                            </v-col>
                            <v-spacer />
                            <v-col class="mt-2">
                                <v-autocomplete
                                    v-model="selectedSchemaConfigs"
                                    multiple
                                    variant="outlined"
                                    label="Schema Configs"
                                    :items="schemaConfigOptions"
                                    density="compact"
                                    clearable
                                    item-title="text"
                                    item-value="value"
                                />
                            </v-col>
                            <v-col class="mt-2">
                                <v-btn
                                    @click="fetchQCSummary"
                                    color="primary"
                                    :disabled="!selectedSchemaConfigs.length"
                                    >{{ qcSummary.length ? "Rerun Query" : "Run Query" }}</v-btn
                                >
                            </v-col>
                            <v-col class="text-right">
                                <v-checkbox
                                    v-model="groupTasks"
                                    label="Group tasks by experiment"
                                    density="compact"
                                    @update:model-value="groupTasks"
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-card-title>
                </v-card>
                <filter-panel
                    v-if="filterOptions && qcSummary.length > 0"
                    :filterOptions="filterOptions"
                    :model-value="filterState"
                    @update:modelValue="onUpdateFilters"
                />
                <v-card v-if="qcSummary.length > 0" class="mt-2">
                    <v-data-table
                        :headers="headers"
                        :items="filteredQCSummary"
                        style="margin-top: 2px; padding-top: 8px"
                        :group-by="groupTasks ? [{ key: 'experiment_grouping' }] : []"
                        multi-sort
                        items-per-page="100"
                        item-value="data_collection_task_id"
                        :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
                    >
                        <template v-slot:group-header="{ item, columns, toggleGroup, isGroupOpen }">
                            <tr>
                                <td :colspan="columns.length">
                                    <v-icon @click="toggleGroup(item)" style="margin: 8px">
                                        {{ isGroupOpen(item) ? "$chevron-down" : "$chevron-right" }}
                                    </v-icon>
                                    <strong>{{ item.value }}</strong>
                                </td>
                            </tr>
                        </template>
                        <template v-slot:item.operator="{ item }">
                            {{ item.operator.join(", ") }}
                        </template>
                        <template v-slot:item.task_status="{ item }">
                            <TaskStatusIcon
                                :task="toTask(item)"
                                @openTaskStatusDialog="openTaskStatusDialog(item)"
                            />
                        </template>
                        <template v-slot:item.status="{ item }">
                            <DataQCStatusChip :status="item.status" />
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-row>
                                <v-icon-btn
                                    color="primary"
                                    @click="showQCDetailDialog(item)"
                                    density="compact"
                                >
                                    <v-icon>$file-eye</v-icon>
                                </v-icon-btn>
                                <v-icon-btn
                                    color="primary"
                                    @click="showUserMeasurementsDialog(item)"
                                    density="compact"
                                >
                                    <v-icon>$account-clock</v-icon>
                                </v-icon-btn>
                            </v-row>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col>
                <v-card>
                    <v-card-title>
                        <v-btn @click="viewQCList">Back to QC Dashboard</v-btn>
                    </v-card-title>
                </v-card>
                <user-measurements-table-form
                    :task-schedule="taskSchedules.get(selectedQCItem.experiment_code)"
                    :user-measurement-set="selectedUserMeasurementSet.userMeasurementSet"
                    :users-with-measurement-sets="
                        selectedUserMeasurementSet.usersWithMeasurementSets
                    "
                    :data-collection-task="selectedUserMeasurementSet.dataCollectionTask"
                    :experiment="selectedExperiment"
                    :protocol="schemaConfigs.get(selectedQCItem.schema_config_id)"
                    @closed="viewQCList"
                />
            </v-col>
        </v-row>

        <DataQCDetailDialog
            v-if="showDetailDialog"
            v-model="showDetailDialog"
            @save="closeDialog"
            @cancel="cancelDialog"
            :qcItem="selectedQCItem"
            :schemaConfig="schemaConfigs.get(selectedQCItem.schema_config_id)"
            :taskSchedule="taskSchedules.get(selectedQCItem.experiment_code)"
            :dataCollectionTask="selectedDataCollectionTask"
            :experiment="selectedExperiment"
        />
        <v-dialog v-model="showUserMeasurementsList">
            <user-measurement-sets-list
                v-if="selectedDataCollectionTask != null"
                :dataCollectionTask="selectedDataCollectionTask"
                :taskSchedule="taskSchedules.get(selectedQCItem.experiment_code)"
                :protocol="schemaConfigs.get(selectedQCItem.schema_config_id)"
                :experiment="selectedExperiment"
                @viewUserMeasurementSet="viewUserMeasurementSet"
                @close="showUserMeasurementsList = false"
            />
        </v-dialog>
        <v-dialog v-model="markTaskAsDoneDialogVisible" max-width="50%">
            <task-field-status-form
                v-if="selectedDataCollectionTask != null"
                :data-collection-task="selectedDataCollectionTask"
                :experiment="selectedExperiment"
                :task-schedule="selectedTaskSchedule"
                @cancelled="closeMarkTaskAsDoneDialog"
                @updatedTaskStatus="updatedTaskStatus"
            />
        </v-dialog>
    </v-container>
</template>

<script>
import { defineComponent } from "vue";

import DataQCDetailDialog from "./DataQCDetailDialog.vue";
import DataQCStatusChip from "./DataQCStatusChip.vue";
import FilterPanel from "@/components/common/FilterPanel.vue";
import { measurementsAPI, taskScheduleAPI } from "@/services/api.services";
import { withLoadingScreen } from "@/lib/loading";
import { mapGetters } from "vuex";
import {
    getDateTimeForTimestampDisplay,
    getISODateOnlyStringInSiteTimeZone,
} from "@/lib/dateUtils";
import { findTaskDayInTaskGroups, getDateForTaskInTaskGroups } from "@/lib/taskUtils";
import { DateTime } from "luxon";
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import TaskStatusIcon from "../Measurements/TaskStatusIcon.vue";
import TaskFieldStatusForm from "../Measurements/TaskFieldStatusForm.vue";
import { thingAPI } from "@/services/things/thingAPI.ts";
import _ from "lodash";
import UserMeasurementSetsList from "../Measurements/UserMeasurementSetsList.vue";
import UserMeasurementsTableForm from "../Measurements/UserMeasurementsTableForm.vue";
import { getTrialCodeFromExperimentName } from "@/lib/experimentMetadata";
import { manageSchemasAPI } from "@/services/schemas/manageSchemasAPI";

const fetchedExperimentObjectsByCode = {};

export default defineComponent({
    components: {
        DataQCDetailDialog,
        DataQCStatusChip,
        FilterPanel,
        TaskStatusIcon,
        TaskFieldStatusForm,
        UserMeasurementSetsList,
        UserMeasurementsTableForm,
    },

    async mounted() {
        await withLoadingScreen(async () => {
            const allSchemaConfigs = await manageSchemasAPI.fetchAllSchemaConfigs();
            this.schemaConfigOptions = allSchemaConfigs.map((config) => ({
                text: config.name,
                value: config.id,
            }));
        });

        if (this.isAuthorizedForOneOf("DataQC:Create")) {
            this.headers.push({ title: "Actions", value: "actions", sortable: false, width: "5%" });
        }
    },

    methods: {
        async fetchQCSummary() {
            await withLoadingScreen(async () => {
                await this.reloadSummary();
                this.filterOptions = buildFilterOptionsFromData(this.qcSummary, this.filterConfig);
            });
        },
        async showQCDetailDialog(item) {
            await this.setSelectedQCItem(item);
            this.showDetailDialog = true;
        },
        async showUserMeasurementsDialog(item) {
            await this.setSelectedQCItem(item);
            this.showUserMeasurementsList = true;
        },
        async setSelectedQCItem(item) {
            await withLoadingScreen(async () => {
                this.selectedQCItem = item;
                const taskSchedule = this.taskSchedules.get(item.experiment_code);
                const dataCollectionTask = taskSchedule.dataCollectionTasks.find(
                    (task) => task.id === item.data_collection_task_id
                );
                this.selectedDataCollectionTask = {
                    ...dataCollectionTask,
                    anticipatedDate: item.anticipated_date,
                    collectionDateStart: item.user_start_date,
                    collectionDateStop: item.user_end_date,
                };
                this.selectedExperiment = await this.buildExperiment(item);
            });
        },
        async buildExperiment(qcItem) {
            const experimentCode = qcItem.experiment_code;
            if (_.isNil(fetchedExperimentObjectsByCode[experimentCode])) {
                await withLoadingScreen(async () => {
                    const { experiment } = await thingAPI.fetchExperimentByCode(experimentCode);
                    const day0 = DateTime.fromISO(experiment.day0, { zone: "utc" });
                    fetchedExperimentObjectsByCode[experimentCode] = { ...experiment, day0 };
                });
            }
            return fetchedExperimentObjectsByCode[experimentCode];
        },
        async closeDialog() {
            this.showDetailDialog = false;
            await this.reloadSummary();
        },
        cancelDialog() {
            this.showDetailDialog = false;
        },
        async reloadSummary() {
            const result = await measurementsAPI.fetchQCSummary(this.selectedSchemaConfigs);
            this.actualUserCollectionDates = result.actualUserCollectionDates;
            this.qcSummary = result.qcSummary;

            for (const item of this.qcSummary) {
                item.experiment_grouping = this.buildExperimentGrouping(item);
                item.experiment_display_code =
                    getTrialCodeFromExperimentName(item.experiment_name) || item.experiment_code;
                item.updated_at = this.getUpdatedTime(item);
                const actualDates = this.actualUserCollectionDates.find(
                    (actualDateItem) =>
                        actualDateItem.data_collection_task_id === item.data_collection_task_id
                );
                if (actualDates) {
                    item.user_day = actualDates.user_day;
                    item.user_start_date = getISODateOnlyStringInSiteTimeZone(
                        actualDates.user_start_date,
                        item.site_time_zone
                    );
                    item.user_end_date = getISODateOnlyStringInSiteTimeZone(
                        actualDates.user_end_date,
                        item.site_time_zone
                    );
                }
            }

            await this.setSchemaConfigs();
            this.setTaskSchedules();
        },
        async setTaskSchedules() {
            const uniqueExperimentCodes = new Set(
                this.qcSummary.map((item) => item.experiment_code)
            );
            const experimentCodes = Array.from(uniqueExperimentCodes);
            const experimentBatches = _.chunk(experimentCodes, 20);
            for (const batch of experimentBatches) {
                const taskSchedules =
                    await taskScheduleAPI.fetchTaskSchedulesForExperimentCodes(batch);
                taskSchedules.forEach((taskSchedule) => {
                    this.taskSchedules.set(taskSchedule.experimentCode, taskSchedule);
                });
            }

            this.setAnticipatedDates();
        },
        setAnticipatedDates() {
            this.qcSummary.forEach((item) => {
                const taskSchedule = this.taskSchedules.get(item.experiment_code);
                const groups = taskSchedule.taskGroups;
                const task = taskSchedule.dataCollectionTasks.find(
                    (task) => task.id === item.data_collection_task_id
                );
                const day0 = DateTime.fromISO(item.experiment_start_date, {
                    zone: "UTC",
                });
                item.anticipated_date = getDateForTaskInTaskGroups(task, groups, day0);
                item.anticipated_day = findTaskDayInTaskGroups(task, groups, day0);
            });
        },
        buildExperimentGrouping(item) {
            let grouping = `${item.experiment_name} (${item.experiment_code})`;
            if (item.investigator) {
                grouping += ` — ${item.investigator}`;
            }
            if (item.operator.length > 0) {
                grouping += ` — ${item.operator.join(", ")}`;
            }
            return grouping;
        },
        async setSchemaConfigs() {
            if (this.schemaConfigs.size === 0) {
                await this.fetchSchemaConfigs();
            }
        },
        async fetchSchemaConfigs() {
            const schemaConfigList = this.qcSummary
                .map((item) => item.schema_config_id)
                .filter((id) => id != null)
                .reduce((acc, val) => (acc.includes(val) ? acc : [...acc, val]), []);

            // Fetch schema configs in batches to avoid creating too large of a request
            const schemaConfigBatches = _.chunk(schemaConfigList, 20);
            const schemaConfigs = [];
            for (const batch of schemaConfigBatches) {
                const batchSchemaConfigs = await manageSchemasAPI.fetchSchemaConfigsByIds(batch);
                schemaConfigs.push(...batchSchemaConfigs);
            }
            schemaConfigs.forEach((schemaConfig) => {
                this.schemaConfigs.set(schemaConfig.id, schemaConfig);
            });
        },
        getUpdatedTime(item) {
            if (item.updated_at) {
                return getDateTimeForTimestampDisplay(new Date(item.updated_at));
            } else if (item.created_at) {
                return getDateTimeForTimestampDisplay(new Date(item.created_at));
            } else {
                return "";
            }
        },
        onUpdateFilters(newFilters) {
            this.filterState = newFilters;
        },
        async openTaskStatusDialog(item) {
            await withLoadingScreen(async () => {
                this.selectedTaskSchedule = this.taskSchedules.get(item.experiment_code);
                this.selectedDataCollectionTask =
                    this.selectedTaskSchedule.dataCollectionTasks.find(
                        (task) => task.id === item.data_collection_task_id
                    );
                this.selectedExperiment = await this.buildExperiment(item);
                this.markTaskAsDoneDialogVisible = true;
            });
        },
        closeMarkTaskAsDoneDialog() {
            this.markTaskAsDoneDialogVisible = false;
            this.selectedDataCollectionTask = null;
            this.selectedExperiment = null;
            this.selectedTaskSchedule = null;
        },
        updatedTaskStatus() {
            this.markTaskAsDoneDialogVisible = false;
            this.reloadSummary();
        },
        viewUserMeasurementSet({
            userMeasurementSet,
            dataCollectionTask,
            taskGroup,
            readoutGroup,
            thingSchema,
            usersWithMeasurementSets,
        }) {
            this.selectedUserMeasurementSet = {
                userMeasurementSet,
                dataCollectionTask,
                taskGroup,
                readoutGroup,
                thingSchema,
                usersWithMeasurementSets,
            };
            this.showUserMeasurementsList = false;
        },
        viewQCList() {
            this.selectedUserMeasurementSet = null;
            this.reloadSummary();
        },
        toTask(item) {
            return {
                id: item.data_collection_task_id,
                fieldStatus: item.field_status,
                type: item.task_type,
            };
        },
    },

    computed: {
        ...mapGetters(["isAuthorizedForOneOf"]),
        filteredQCSummary() {
            return filterData(this.qcSummary, this.filterState, this.filterConfig);
        },
    },

    watch: {
        groupTasks() {
            if (this.groupTasks) {
                this.headers = this.headers.filter(
                    (header) => header.value !== "experiment_display_code"
                );
            } else {
                this.headers.splice(1, 0, {
                    title: "Experiment",
                    value: "experiment_display_code",
                    width: "15%",
                    sortable: true,
                });
            }
        },
    },

    data() {
        return {
            showDetailDialog: false,
            showUserMeasurementsList: false,
            headers: [
                // this first header prevents the group-by from inserting a column header titled "Group"
                {
                    title: "",
                    key: "data-table-group",
                    width: "0px",
                    align: "center",
                    sortable: false,
                },
                { title: "Task Group", value: "task_group_name", width: "8%", sortable: true },
                {
                    title: "Readout Group",
                    value: "readout_group_name",
                    width: "10%",
                    sortable: true,
                },
                { title: "Anticipated Day", value: "anticipated_day", width: "3%", sortable: true },
                { title: "Collection Day", value: "user_day", width: "5%", sortable: true },
                {
                    title: "Anticipated Date",
                    value: "anticipated_date",
                    width: "5%",
                    sortable: true,
                },
                {
                    title: "Collection Date Start",
                    value: "user_start_date",
                    width: "5%",
                    sortable: true,
                },
                {
                    title: "Collection Date Stop",
                    value: "user_end_date",
                    width: "5%",
                    sortable: true,
                },
                { title: "Task Status", value: "task_status", width: "3%", sortable: true },
                { title: "QC Status", value: "status", width: "10%", sortable: true },
                { title: "Data Notes", value: "comments", width: "15%", sortable: true },
                {
                    title: "Investigator Comments",
                    value: "investigator_comments",
                    width: "15%",
                    sortable: true,
                },
                {
                    title: "Last Updated",
                    value: "updated_at",
                    width: "8%",
                    sortable: true,
                },
                { title: "Updated By", value: "updated_by", width: "8%", sortable: true },
            ],
            qcSummary: [],
            actualUserCollectionDates: [],
            schemaConfigs: new Map(),
            taskSchedules: new Map(),
            selectedQCItem: null,
            selectedDataCollectionTask: null,
            selectedExperiment: null,
            selectedTaskSchedule: null,
            markTaskAsDoneDialogVisible: false,
            filterState: {},
            filterConfig: [
                { name: "experimentGroup", prop: "experiment_group_name" },
                { name: "schemaConfig", prop: "schema_config_name" },
                { name: "investigator", prop: "investigator", noneOption: true },
                { name: "operator", prop: "operator", multiple: true },
                { name: "studyDirector", prop: "study_director", noneOption: true },
                { name: "taskGroup", prop: "task_group_name" },
                { name: "readoutGroup", prop: "readout_group_name" },
                { name: "QCStatus", prop: "status" },
                { name: "siteCountry", prop: "site_country", noneOption: true },
                { name: "taskStatus", prop: "field_status", noneOption: true },
                { name: "experimentStatus", prop: "experiment_status", noneOption: true },
            ],
            filterOptions: null,
            selectedUserMeasurementSet: null,
            groupTasks: true,
            selectedSchemaConfigs: [],
            schemaConfigOptions: [],
        };
    },
});
</script>
<style scoped>
.no-text-field-details:deep(.v-text-field__details) {
    display: none;
}
</style>
